import { useEffect, useRef, useState } from 'react';
import { useAuth } from '../auth';
import { useI18n } from '../i18n';
import './StreakPanel.css';

const API = import.meta.env.VITE_API_URL || 'http://localhost:8000';

interface StreakInfo {
  current_streak: number;
  longest_streak: number;
  streak_active_today: boolean;
}

export function StreakPanel() {
  const { token } = useAuth();
  const { t } = useI18n();
  const [info, setInfo] = useState<StreakInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    if (!token) { setLoading(false); return; }
    fetch(`${API}/stats/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : null)
      .then(d => {
        if (!mounted.current || !d) return;
        if (typeof d.current_streak === 'number') {
          setInfo({ current_streak: d.current_streak, longest_streak: d.longest_streak ?? 0, streak_active_today: !!d.streak_active_today });
        }
      })
      .catch(() => {})
      .finally(() => { if (mounted.current) setLoading(false); });
    return () => { mounted.current = false; };
  }, [token]);

  const home = t.home as any;
  const streak = info?.current_streak ?? 0;
  const longest = info?.longest_streak ?? 0;
  const isActive = !!info?.streak_active_today;
  const isRecord = streak > 0 && streak >= longest;

  const subText = streak === 0
    ? (home.streakAtRisk || '')
    : isActive ? (home.streakActive || '') : (home.streakAtRisk || '');

  const dots = Array.from({ length: 7 }, (_, i) => {
    const filled = streak > 0 && i >= 7 - Math.min(7, streak);
    const isToday = i === 6;
    return { filled, isToday };
  });

  if (loading) {
    return <div className="sp-loading"><span className="sp-spinner" /></div>;
  }

  return (
    <div className="streak-panel">
      <div className="sp-title">{(home.streakTitle || 'STREAK').toUpperCase()}</div>

      {/* Hero block */}
      <div className="sp-hero">
        <div className="sp-glow-ring sp-ring1" />
        <div className="sp-glow-ring sp-ring2" />
        <div className="sp-hero-inner">
          <span className="sp-flame">🔥</span>
          <div className="sp-number-col">
            <span className="sp-big-number">{streak}</span>
            <span className="sp-days-label">{streak === 1 ? (home.streakDay || 'day') : (home.streakDaysWord || 'days')}</span>
          </div>
        </div>
      </div>

      {/* Status pill */}
      {subText && (
        <div className={`sp-status ${isActive ? 'sp-status--active' : 'sp-status--risk'}`}>
          <span>{isActive ? '✅' : '⚠️'}</span>
          <span>{subText}</span>
        </div>
      )}

      {/* 7-day dot strip */}
      <div className="sp-week-strip">
        {dots.map((d, i) => (
          <div key={i} className={`sp-dot ${d.filled ? 'sp-dot--filled' : ''} ${d.isToday ? 'sp-dot--today' : ''}`}>
            {d.filled && <span>🔥</span>}
          </div>
        ))}
      </div>

      {/* Trophy card (longest) */}
      <div className="sp-trophy-card">
        <div className="sp-trophy-shine" />
        <div className="sp-trophy-content">
          <div className="sp-trophy-medal">🏆</div>
          <div className="sp-trophy-text">
            <div className="sp-trophy-label">{(home.streakLongestLabel || 'BEST RECORD').toUpperCase()}</div>
            <div className="sp-trophy-value-row">
              <span className="sp-trophy-value">{longest}</span>
              <span className="sp-trophy-unit">{longest === 1 ? (home.streakDay || 'day') : (home.streakDaysWord || 'days')}</span>
            </div>
          </div>
          {isRecord && <div className="sp-record-badge">{home.streakNewRecord}</div>}
        </div>
      </div>
    </div>
  );
}
